import { snakeCase, camelCase } from 'lodash-es';
import { useState, useEffect } from 'react';
import { Extension, CommandProps } from '@tiptap/core';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Autocomplete,
  Paper,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import GrayButton from 'components/GrayButton';

export type RichTextSnippet = {
  name: string;
  category?: string;
  template: string;
  placeholders: Record<string, string>; // key: variable, value: human-friendly name
};

export type SnippetDialogProps = {
  snippets: RichTextSnippet[];
  open: boolean;
  onClose: () => void;
  onConfirm: (snippet: RichTextSnippet, values: Record<string, string>) => void;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    insertSnippet: {
      insertSnippet(
        snippet: RichTextSnippet,
        values: Record<string, string>,
      ): ReturnType;
    };
  }
}

export const SnippetExtension = Extension.create({
  name: 'snippet',
  addCommands: () => {
    return {
      insertSnippet:
        (snippet: RichTextSnippet, values: Record<string, string>) =>
        ({ commands }: CommandProps) => {
          const text = Object.keys(snippet.placeholders).reduce((acc, ph) => {
            const val = values[camelCase(ph)];
            const valToInsert =
              val && val.trim() ? val : `[${snippet.placeholders[ph]}]`;

            return acc.replace(
              new RegExp(
                `{{${ph}}}|{{${snakeCase(ph)}}}|{{${camelCase(ph)}}}`,
                'g',
              ),
              valToInsert,
            );
          }, snippet.template);

          return commands.insertContent(text);
        },
    };
  },
});

const renderSnippetPreview = (
  template: string,
  placeholders: Record<string, string>,
  values: Record<string, string>,
  theme: Theme,
): JSX.Element[] => {
  return template.split(/({{\w+}})/g).map((part, idx) => {
    const match = part.match(/^{{(\w+)}}$/);

    if (match) {
      const ph = camelCase(match[1]);
      const val = values[ph];
      return (
        <span
          // eslint-disable-next-line react/no-array-index-key
          key={`ph-${ph}-${idx}`}
          style={{
            wordBreak: 'break-word',
            background: theme.palette.primary.light,
            padding: '0 2px',
            borderRadius: '2px',
            whiteSpace: 'pre-wrap',
          }}
        >
          {val && val.trim() ? val : `[${placeholders[ph]}]`}
        </span>
      );
    }

    // Static text part
    return (
      <span
        key={`txt-${part}`}
        style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      >
        {part}
      </span>
    );
  });
};

const getOrderedPlaceholders = (snippet: RichTextSnippet): string[] => {
  if (!snippet) return [];

  const seen = new Set<string>();
  return snippet.template
    .split(/({{\w+}})/g)
    .filter((part) => Boolean(part.match(/^{{(\w+)}}$/)))
    .map(camelCase)
    .filter((ph) => {
      // Only include unique placeholders but keep order
      if (seen.has(ph)) return false;
      seen.add(ph);
      return true;
    });
};

export const SnippetDialog = ({
  snippets,
  open,
  onClose,
  onConfirm,
}: SnippetDialogProps): JSX.Element | null => {
  const { t } = useTranslation();
  const [selectedSnippet, setSelectedSnippet] =
    useState<RichTextSnippet | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const theme = useTheme();

  // When snippets load or dialog opens, reset selectedSnippet and values
  useEffect(() => {
    if (open && snippets.length > 0) {
      setSelectedSnippet(null);
      setValues({});
    }
  }, [open, snippets]);

  const allPlaceholdersFilled = (): boolean => {
    if (!selectedSnippet) return false;

    const placeholders = Object.keys(selectedSnippet.placeholders);
    if (placeholders.length === 0) return true;

    return placeholders.every((ph) => values[ph]?.trim());
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('richTextEditor.addSnippet')}</DialogTitle>
      <DialogContent>
        <Autocomplete
          options={snippets}
          groupBy={(option) => option.category || 'General'}
          getOptionLabel={(option) => option.name}
          value={selectedSnippet}
          onChange={(_, newValue) => {
            if (newValue) {
              setSelectedSnippet(newValue);
              setValues(
                Object.keys(newValue.placeholders).reduce(
                  (acc, ph) => ({ ...acc, [ph]: '' }),
                  {},
                ),
              );
            }
          }}
          autoHighlight
          openOnFocus
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('richTextEditor.snippetSelect')}
              fullWidth
              margin="normal"
              autoFocus
            />
          )}
        />
        {selectedSnippet &&
          Object.keys(selectedSnippet.placeholders).length > 0 && (
            <>
              {Array.from(getOrderedPlaceholders(selectedSnippet)).map((ph) => (
                <TextField
                  key={ph}
                  label={selectedSnippet.placeholders[ph]}
                  value={values[ph] || ''}
                  onChange={(e) =>
                    setValues({ ...values, [ph]: e.target.value })
                  }
                  onKeyDown={(e) => {
                    if (
                      e.key === 'Enter' &&
                      !e.shiftKey &&
                      allPlaceholdersFilled()
                    ) {
                      e.preventDefault();
                      onConfirm(selectedSnippet, values);
                    }
                  }}
                  fullWidth
                  margin="normal"
                />
              ))}
            </>
          )}
        {selectedSnippet && (
          <>
            {/* Preview Section */}
            <Typography
              variant="body1"
              component="h3"
              marginY={2}
              fontWeight="bold"
            >
              <b>{t('richTextEditor.snippetPreview')}:</b>
            </Typography>
            <Paper
              elevation={0}
              sx={{
                padding: 2,
                backgroundColor: theme.palette.grey[100],
              }}
              data-testid="snippet-preview"
            >
              {renderSnippetPreview(
                selectedSnippet.template,
                selectedSnippet.placeholders,
                values,
                theme,
              )}
            </Paper>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <GrayButton onClick={onClose}>{t('common.cancel')}</GrayButton>
        <Button
          onClick={() => {
            if (selectedSnippet) {
              onConfirm(selectedSnippet, values);
            }
          }}
          color="primary"
          disabled={!allPlaceholdersFilled()}
        >
          {t('richTextEditor.snippetDone')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
